import { gsap } from 'gsap';
import MotionPathPlugin from 'gsap/MotionPathPlugin';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, NavigateOptions, To, useLocation } from 'react-router-dom';
import styled from 'styled-components';

import { Button } from '@column/column-ui-kit';

import { CheckYourEmail, Logo, Panel, animateLogo } from '../_components';
import { ROUTE as APP_ROUTE, ROUTE_PREFIX as APP_ROUTE_PREFIX } from '~/app/routes';
import { useQueryParams } from '~/hooks';
import { FeatureFlag, useFeatureFlag } from '~/lib/flags';
import { useNavigate } from '~/lib/navigation';
import {
  LoginAuthMethod,
  getPendingPlatformInvite,
  getPlatformInviteRedirect,
  shouldPromptMFASetup,
} from '~/lib/session';
import { ONBOARDING_QUERY_PARAM, ROUTE as PUBLIC, ROUTE } from '~/public/routes';
import {
  ChallengeMFAResponse,
  ChallengeName,
  DashboardLoginResponse,
  EmailConfirmationStatus,
  MFAStatus,
  capturePlaidAuthFlowParams,
  getPlaidAuthFlow,
  stampPlaidAuthFlow,
} from '~/repositories';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { Divider, Flex, Hint, Paragraph } from '~/styles';

import { MultifactorAuthForm, PasskeyAuthButton, PasswordAuthForm } from './_partials';

interface PageLoginProps {
  isAuthFlow?: boolean;
}

export type LoginQueryParams = {
  [key: string]: string;
  redirect: string;
};

enum LoginView {
  PASSWORD = 'password',
  MFA = 'mfa',
  EMAIL_CONFIRMATION = 'email_confirmation',
}

interface PassswordState {
  view: LoginView.PASSWORD;
}

// The MFA challenge a confirmed magic link still owes, handed over through history state.
export interface ResumeMFAState {
  challengeName: ChallengeName;
  phoneNumber?: string;
  sessionId: string;
  resumeOnboarding?: boolean;
}

interface MultifactorState extends ResumeMFAState {
  view: LoginView.MFA;
}

interface EmailConfirmationState {
  view: LoginView.EMAIL_CONFIRMATION;
}

type LoginState = PassswordState | MultifactorState | EmailConfirmationState;

interface LoginLocationState {
  resumeMFA?: ResumeMFAState;
}

const LOGIN_EMAIL_CONFIRMATION_REQUIRED_ERROR_CODE = 'login_email_confirmation_required';

const AdditionalLogo = styled(Logo)`
  margin-left: -12px;
  transform-origin: 0 0;
  overflow: visible;

  & > path {
    --logo-path-opacity: 0;
    --logo-path-x: 16px;
    --logo-path-y: 24px;
    opacity: var(--logo-path-opacity);
    transform: translate(var(--logo-path-x), var(--logo-path-y)) translateZ(0);
  }
`;

const HiddenLogo = styled(Logo)`
  opacity: 0;
`;

const LogoWrapper = styled.div`
  position: absolute;
  left: 50%;
  top: 0;
`;

const normalizeRedirectUrl = (url: string | null): string | undefined => {
  if (!url) {
    return undefined;
  }

  const isAbsoluteUrl = (testUrl: string): boolean => /^(https?:\/\/)/i.test(testUrl);
  const getRelativeUrl = (testUrl: string): string => (testUrl.startsWith('/') ? testUrl : `/${testUrl}`);

  if (isAbsoluteUrl(url)) {
    return url;
  }

  return getRelativeUrl(url);
};

export const PageLogin: React.FC<PageLoginProps> = ({ isAuthFlow }) => {
  const addDangerNotification = useNotificationStore((s) => s.addDangerNotification);
  const passkeyAuthEnabled = useFeatureFlag(FeatureFlag.EnablePasskeyAuth);
  const { setSession, loadSession, setParams, params } = useSessionStore();
  const { getQueryParam } = useQueryParams<LoginQueryParams>();
  const logoWrapperRef = useRef<HTMLDivElement>(null);
  const logoRef = useRef<SVGSVGElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();

  const redirectUrl = normalizeRedirectUrl(getQueryParam('redirect'));
  const pendingPlatformInvite = useMemo(() => getPendingPlatformInvite(redirectUrl), [redirectUrl]);

  const resumeMFA = (location.state as LoginLocationState | null)?.resumeMFA;
  const [loginState, setLoginState] = useState<LoginState>(
    resumeMFA ? { view: LoginView.MFA, ...resumeMFA } : { view: LoginView.PASSWORD }
  );
  const isResumedMFA = useRef(Boolean(resumeMFA));

  useEffect(() => {
    if (resumeMFA) {
      isResumedMFA.current = true;
      setLoginState({ view: LoginView.MFA, ...resumeMFA });
    } else if (isResumedMFA.current) {
      isResumedMFA.current = false;
      setLoginState({ view: LoginView.PASSWORD });
    }
  }, [location.key]);

  const navigateWithAnimation = useCallback((to: To, options?: NavigateOptions) => {
    const logo = logoRef.current;
    const logoWrapper = logoWrapperRef.current;
    const panel = panelRef.current;

    if (!logo || !logoWrapper || !panel) {
      navigate(to, options);
      return;
    }

    // Lock the panel dimensions to prevent layout shifts during animation
    const panelRect = panel.getBoundingClientRect();
    panel.style.width = `${panelRect.width}px`;
    panel.style.height = `${panelRect.height}px`;
    panel.style.overflow = 'hidden';
    panel.style.pointerEvents = 'none';

    animateLogo(logo, logoWrapper, panel, gsap, MotionPathPlugin, () => navigate(to, options));
  }, []);

  const onLogin = useCallback(
    (
      sessionId: string,
      dashboardUserId: string,
      mfaStatus: MFAStatus,
      loginAuthMethod: LoginAuthMethod,
      resumeOnboarding = false
    ) => {
      setSession(sessionId, dashboardUserId, { loginAuthMethod, mfaStatus });

      const replace = isResumedMFA.current;

      loadSession()
        .then(() => {
          if (isAuthFlow && getPlaidAuthFlow(params)) {
            navigate(PUBLIC.PLAID_SELECT, { replace });
            return;
          }

          const platformInviteRedirect = getPlatformInviteRedirect(pendingPlatformInvite);
          if (platformInviteRedirect) {
            navigate(platformInviteRedirect, {
              replace,
              state: { from: 'auth' },
            });
            return;
          }

          if (shouldPromptMFASetup(mfaStatus, loginAuthMethod)) {
            navigate(resumeOnboarding ? `${PUBLIC.SETUP_MFA}?${ONBOARDING_QUERY_PARAM}=1` : PUBLIC.SETUP_MFA, {
              replace,
            });
            return;
          }

          if (resumeOnboarding) {
            navigate(PUBLIC.REGISTER_FINANCIAL_PRODUCTS, { replace: true });
            return;
          }

          const destinationUrl = redirectUrl || APP_ROUTE.ROOT;

          if (destinationUrl.startsWith(APP_ROUTE_PREFIX)) {
            navigateWithAnimation(destinationUrl, {
              replace,
              state: { from: 'auth' },
            });
            return;
          }
          navigate(destinationUrl, {
            replace,
            state: { from: 'auth' },
          });
        })
        .catch((error) => {
          addDangerNotification({
            content: error.message,
            display: 'page',
          });
        });
    },
    [navigateWithAnimation, isAuthFlow, params, pendingPlatformInvite, redirectUrl]
  );

  const onEmailConfirmationRequired = useCallback(() => {
    const stamped = isAuthFlow ? stampPlaidAuthFlow(params) : undefined;
    if (stamped) {
      setParams(stamped);
    }
    setLoginState({ view: LoginView.EMAIL_CONFIRMATION });
  }, [isAuthFlow, params]);

  const onAuthSuccess = useCallback(
    (response: DashboardLoginResponse, loginAuthMethod: LoginAuthMethod) => {
      if (response.challengeName && response.challengeName !== ChallengeName.CHALLENGE_NONE) {
        setLoginState({
          view: LoginView.MFA,
          challengeName: response.challengeName,
          phoneNumber: response.phoneNumber,
          sessionId: response.sessionId,
        });
        return;
      }

      if (response.emailConfirmationStatus === EmailConfirmationStatus.UNVERIFIED) {
        onEmailConfirmationRequired();
        return;
      }

      onLogin(response.sessionId, response.dashboardUserId, response.mfaStatus, loginAuthMethod);
    },
    [onLogin, onEmailConfirmationRequired]
  );

  const onMultifactorSuccess = useCallback(
    (sessionId: string, response: ChallengeMFAResponse) => {
      const resumeOnboarding = loginState.view === LoginView.MFA && loginState.resumeOnboarding === true;

      if (response.emailConfirmationStatus === EmailConfirmationStatus.UNVERIFIED) {
        onEmailConfirmationRequired();
        return;
      }

      onLogin(sessionId, response.dashboardUserId, response.mfaStatus, LoginAuthMethod.PASSWORD, resumeOnboarding);
    },
    [loginState, onLogin, onEmailConfirmationRequired]
  );

  const onMultifactorCancel = useCallback(() => {
    setLoginState({ view: LoginView.PASSWORD });

    if (isResumedMFA.current) {
      navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
    }
  }, [location.pathname, location.search]);

  const onPasswordAuthError = useCallback(
    (error: unknown): boolean => {
      if (
        typeof error === 'object' &&
        error !== null &&
        'code' in error &&
        typeof error.code === 'string' &&
        error.code === LOGIN_EMAIL_CONFIRMATION_REQUIRED_ERROR_CODE
      ) {
        onEmailConfirmationRequired();
        return true;
      }

      return false;
    },
    [onEmailConfirmationRequired]
  );

  useEffect(() => {
    if (isAuthFlow || !params) {
      return;
    }

    setParams(undefined);
  }, [isAuthFlow, params, setParams]);

  useEffect(() => {
    if (!isAuthFlow) {
      return;
    }

    const plaidAuthFlowParams = capturePlaidAuthFlowParams(new URLSearchParams(location.search));
    if (plaidAuthFlowParams) {
      setParams(plaidAuthFlowParams);
    }
  }, [isAuthFlow, location.search, setParams]);

  return (
    <>
      {!isAuthFlow && (
        <LogoWrapper ref={logoWrapperRef}>
          <AdditionalLogo size="24px" variant="full" ref={logoRef} />
        </LogoWrapper>
      )}

      <Panel ref={panelRef}>
        {!isAuthFlow && <HiddenLogo size="24px" />}
        {loginState.view === LoginView.PASSWORD && (
          <Flex flexDirection="column" gap={16}>
            <PasswordAuthForm
              inviteCode={pendingPlatformInvite?.inviteCode}
              showForgotPassword={!isAuthFlow}
              redirectUrl={redirectUrl}
              onError={onPasswordAuthError}
              onSuccess={(response) => onAuthSuccess(response, LoginAuthMethod.PASSWORD)}
            />
            {passkeyAuthEnabled && (
              <>
                <Divider>or</Divider>
                <PasskeyAuthButton onSuccess={(response) => onAuthSuccess(response, LoginAuthMethod.PASSKEY)} />
              </>
            )}
          </Flex>
        )}
        {loginState.view === LoginView.MFA && (
          <MultifactorAuthForm
            challengeName={loginState.challengeName}
            phoneNumber={loginState.phoneNumber}
            onCancel={onMultifactorCancel}
            onSuccess={(response) => onMultifactorSuccess(loginState.sessionId, response)}
          />
        )}
        {loginState.view === LoginView.EMAIL_CONFIRMATION && (
          <CheckYourEmail
            footerLabel="Back to login"
            onFooterClick={() => setLoginState({ view: LoginView.PASSWORD })}
          />
        )}
        {loginState.view === LoginView.PASSWORD &&
          !isAuthFlow &&
          (passkeyAuthEnabled ? (
            <Hint>
              Don't have a Column account?{' '}
              <Link
                className="inline text-gray-500 no-underline transition-colors hover:text-gray-600"
                to={ROUTE.REGISTER}
              >
                Create one
              </Link>
            </Hint>
          ) : (
            <Flex flexDirection="column" gap={16}>
              <Paragraph size="small">Don't have a Column account?</Paragraph>
              <Button variant="secondary" onClick={() => navigate(ROUTE.REGISTER)}>
                Create New Account
              </Button>
            </Flex>
          ))}
      </Panel>
    </>
  );
};
